import { useEffect, useId, useRef, useState } from "react";

const ROTULOS = ["Superior esquerdo", "Superior direito", "Inferior direito", "Inferior esquerdo"];

const limitar = valor => Math.min(1, Math.max(0, valor));

/**
 * Mostra a imagem com 4 cantos arrastáveis (mouse ou toque) para marcar a área a recortar — a região é
 * depois "endireitada" por recortarPerspectiva. `pontos` normalizados (0–1), na ordem: superior esquerdo,
 * superior direito, inferior direito, inferior esquerdo.
 */
export default function RecorteQuatroPontos({ blob, pontos, onChange, desabilitado = false }) {
    const areaRef = useRef(null);
    const [url, setUrl] = useState(null);
    const [arrastando, setArrastando] = useState(null);
    // Uma máscara por instância — há uma imagem por anexo na mesma janela.
    const idMascara = `mascara-recorte-${useId().replace(/:/g, "")}`;

    useEffect(() => {
        const objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [blob]);

    function moverPara(indice, evento) {
        const area = areaRef.current?.getBoundingClientRect();
        if (!area) return;
        const ponto = {
            x: limitar((evento.clientX - area.left) / area.width),
            y: limitar((evento.clientY - area.top) / area.height),
        };
        onChange(pontos.map((p, i) => (i === indice ? ponto : p)));
    }

    function iniciar(indice, evento) {
        if (desabilitado) return;
        evento.preventDefault();
        evento.currentTarget.setPointerCapture(evento.pointerId);
        setArrastando(indice);
    }

    function mover(indice, evento) {
        if (arrastando !== indice) return;
        moverPara(indice, evento);
    }

    function soltar(evento) {
        if (arrastando === null) return;
        evento.currentTarget.releasePointerCapture?.(evento.pointerId);
        setArrastando(null);
    }

    const poligono = pontos.map(p => `${p.x * 100},${p.y * 100}`).join(" ");

    return (
        <div className="flex justify-center bg-muted/40 rounded-lg p-3">
            {/* A área tem exatamente o tamanho da imagem renderizada — os cantos são relativos a ela. */}
            <div ref={areaRef} className="relative inline-block select-none" style={{ touchAction: "none" }}>
                {url && (
                    <img src={url} alt="" draggable={false} className="block max-h-[60vh] max-w-full pointer-events-none" />
                )}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <defs>
                        <mask id={idMascara}>
                            <rect x="0" y="0" width="100" height="100" fill="white" />
                            <polygon points={poligono} fill="black" />
                        </mask>
                    </defs>
                    {/* Escurece o que fica fora do recorte. */}
                    <rect x="0" y="0" width="100" height="100" fill="rgba(0,0,0,0.45)" mask={`url(#${idMascara})`} />
                    <polygon points={poligono} fill="none" stroke="#2563eb" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                </svg>
                {pontos.map((p, i) => (
                    <button
                        key={ROTULOS[i]}
                        type="button"
                        title={`${ROTULOS[i]} — arraste para ajustar`}
                        aria-label={`Canto ${ROTULOS[i]}`}
                        onPointerDown={e => iniciar(i, e)}
                        onPointerMove={e => mover(i, e)}
                        onPointerUp={soltar}
                        onPointerCancel={soltar}
                        className={`absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-white shadow-md ${arrastando === i ? "bg-primary scale-110" : "bg-primary/80"} ${desabilitado ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing"}`}
                        style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, touchAction: "none" }}
                    />
                ))}
            </div>
        </div>
    );
}
